import { formatDateAsYMD } from '@whistler-ui/k2/helpers/dateFormatter';
import { toQueryString } from '@whistler-ui/k2/helpers/parsers';
import { tryCatch } from '@whistler-ui/k2/helpers/servicesHelper';

import { API_ENDPOINTS, REPORTS_ANALYTICS_TYPES } from '../constants';
import { Filter } from '../store';
import {
  AnalyticsType,
  BillingBreakdownResponse,
  BillingDataResponse,
  OverviewResponse,
  RCSReportParams,
  RCSReportResponse,
  ServiceStatusResponse,
  TUseCaseTag,
  UseCaseReportsResponse,
} from '../types';
import { requestWithOptions } from './helper';

const basePath = API_ENDPOINTS.REPORTING;

type EndpointType =
  | 'top-level-statistics'
  | 'activation-mechanism'
  | 'failed-attempts'
  | 'user-type'
  | 'device-type'
  | 'transfer-type'
  | 'success-funnel'
  | 'step-by-step-performance'
  | 'service-quality'
  | 'per-user';

interface FetchAnalyticsSettings<E = any> {
  endpoint: EndpointType;
  environmentId: string;
  filters: Filter;
  errorResponse?: E;
  analyticsType?: AnalyticsType;
}

// Utility function to fetch analytics data with UTC date range
export const fetchAnalyticsData = async <T, E = any>(
  settings: FetchAnalyticsSettings<E>,
): Promise<T | E> => {
  const {
    endpoint,
    environmentId,
    filters,
    errorResponse,
    analyticsType = REPORTS_ANALYTICS_TYPES.TRANSFER, // Default to 'transfer-analytics' if not provided
  } = settings;

  const { data, error } = await tryCatch(async () => {
    // Get ISO date range
    const start_date = formatDateAsYMD(filters?.startDate);
    const end_date = formatDateAsYMD(filters?.endDate);

    // Validate formatted dates
    if (
      !start_date ||
      !end_date ||
      typeof start_date !== 'string' ||
      typeof end_date !== 'string'
    ) {
      throw new Error('Invalid date range: formatted dates are missing');
    }

    const flowAction = {
      watch_activation: 'watch_websheet_activation',
      // watch_transfer: '_watch_websheet_transfer', Assuming this is the correct suffix for watch transfer
    } as Record<string, string>;

    // Construct query parameters for use_case_type
    // platform and action are optional and only relevant for Watch_Activation
    const useCaseType =
      filters?.platform && filters?.action
        ? `${filters.platform}_${flowAction[filters.action]}`
        : undefined;

    // Construct query parameters with URLSearchParams
    const urlParams = new URLSearchParams({
      start_date,
      end_date,
      ...(filters.platform && { platform: filters.platform }), // Only include platform if it's truthy
      ...(useCaseType && { use_case_type: useCaseType }), // Only include use_case_type if it's truthy
    }).toString();

    // Construct the full API path
    const path = `${API_ENDPOINTS.REPORTING}/${analyticsType}/${environmentId}/${endpoint}?${urlParams}`;

    const options: RequestInit = { method: 'GET' };
    const res = await requestWithOptions(path, options);
    return res;
  });

  if (error) {
    // If errorResponse is provided, return it; otherwise, throw an error
    if (errorResponse !== undefined) {
      return errorResponse;
    }
    throw error;
  }

  return data as T;
};

export const getBillingData = async (environmentId: string, months: string) => {
  const path = `${basePath}/${environmentId}/billing_information?months=${months}`;
  const options = { method: 'GET' };
  const result = await requestWithOptions(path, options);
  return result as BillingDataResponse;
};

export const getBillingBreakdown = async (
  environmentId: string,
  month: string,
) => {
  const path = `${basePath}/${environmentId}/billing_breakdown?months=${month}`;
  const options = { method: 'GET' };
  const result = await requestWithOptions(path, options);
  return result as BillingBreakdownResponse;
};

export const getRCSReports = async (
  environmentId: string,
  params: RCSReportParams,
): Promise<RCSReportResponse> => {
  const urlParams = toQueryString(params);
  const path = `${API_ENDPOINTS.REPORTING}/${environmentId}/rcs/report${
    urlParams ? `?${urlParams}` : ''
  }`;
  const result = await requestWithOptions(path, { method: 'GET' });
  return result as RCSReportResponse;
};

export const getServiceStatus = async () => {
  const path = `${basePath}/uptime_history`;
  const options = { method: 'GET' };
  const result = await requestWithOptions(path, options);
  return result as ServiceStatusResponse;
};

export const getReportsOverview = async (
  environmentId: string,
  from: Date,
  to: Date,
  platform: string,
) => {
  const PLATFORM =
    platform === 'all' ? '' : `&platform=${platform.toUpperCase()}`;
  const path = `${basePath}/${environmentId}/reports_overview?start_date=${formatDateAsYMD(
    from,
  )}&end_date=${formatDateAsYMD(to)}${PLATFORM}`;
  const options = { method: 'GET' };
  const result = await requestWithOptions(path, options);
  return result as OverviewResponse;
};

export const getOverview = async (environmentId: string, days: number) => {
  const result = await requestWithOptions(
    `${basePath}/${environmentId}/reports_overview?days=${days}`,
    { method: 'GET' },
  );

  return result as OverviewResponse;
};

export const getReportsUseCase = async (
  environmentId: string,
  useCase: TUseCaseTag,
  from: Date | string,
  to: Date | string,
) => {
  const path = `${basePath}/${environmentId}/reports/${useCase}?start_date=${formatDateAsYMD(
    from,
  )}&end_date=${formatDateAsYMD(to)}`;
  const options = { method: 'GET' };
  const result = await requestWithOptions(path, options);
  return result as UseCaseReportsResponse;
};
